/**
 * MetadataStore - MobX store for managing authors and teams metadata
 *
 * This store:
 * - Loads authors.json and teams.json from /data directory
 * - Provides access to author and team mappings
 * - Can be initialized independently or as part of the overall data loading
 */

import { makeAutoObservable, runInAction } from "mobx"
import { fetchJson } from "../lib/fetch"
import { logger } from "../lib/logger"
import type { AuthorsMap, GroupsMap, TeamsMap } from "../types/data"

export class MetadataStore {
  // Loading state
  isLoading = false
  loadError: string | null = null

  // Metadata
  authors: AuthorsMap = {}
  teams: TeamsMap = {}
  groups: GroupsMap = {}

  constructor() {
    makeAutoObservable(this)
  }

  /**
   * Initialize the store by loading metadata files
   */
  async initialize(): Promise<void> {
    if (this.isLoading) {
      logger.debug("MetadataStore already initializing, skipping", {
        component: "MetadataStore",
      })
      return
    }

    try {
      runInAction(() => {
        this.isLoading = true
        this.loadError = null
      })

      logger.info("Initializing MetadataStore...", {
        component: "MetadataStore",
      })
      logger.track("metadata_store_init_started")

      // Load all metadata files in parallel
      const [authors, teams, groups] = await Promise.all([
        fetchJson<AuthorsMap>("/data/authors.json").catch((error) => {
          logger.warn("Failed to load authors.json, using empty map", {
            component: "MetadataStore",
            error,
          })
          return {} as AuthorsMap
        }),
        fetchJson<TeamsMap>("/data/teams.json").catch((error) => {
          logger.warn("Failed to load teams.json, using empty map", {
            component: "MetadataStore",
            error,
          })
          return {} as TeamsMap
        }),
        fetchJson<GroupsMap>("/data/groups.json").catch((error) => {
          logger.warn("Failed to load groups.json, using empty map", {
            component: "MetadataStore",
            error,
          })
          return {} as GroupsMap
        }),
      ])

      runInAction(() => {
        this.authors = authors
        this.teams = teams
        this.groups = groups
      })

      logger.info("MetadataStore initialized successfully", {
        component: "MetadataStore",
        authorsCount: Object.keys(authors).length,
        teamsCount: Object.keys(teams).length,
        groupsCount: Object.keys(groups).length,
      })
      logger.track("metadata_store_init_success", {
        authorsCount: Object.keys(authors).length,
        teamsCount: Object.keys(teams).length,
        groupsCount: Object.keys(groups).length,
      })
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to initialize MetadataStore"

      runInAction(() => {
        this.loadError = errorMessage
      })

      logger.error("Failed to initialize MetadataStore", error, {
        component: "MetadataStore",
      })
      logger.track("metadata_store_init_failed", { error: errorMessage })

      throw error
    } finally {
      runInAction(() => {
        this.isLoading = false
      })
    }
  }

  /**
   * Get author handle by ID
   */
  getAuthor(authorId: string): string {
    return this.authors[authorId] || authorId
  }

  /**
   * Get team by author handle (GitHub username)
   */
  getTeam(authorHandle: string): string | undefined {
    return this.teams[authorHandle]
  }

  /**
   * Get all authors as an array
   */
  get allAuthors(): Array<{ id: string; handle: string }> {
    return Object.entries(this.authors).map(([id, handle]) => ({ id, handle }))
  }

  /**
   * Get all teams as an array
   *
   * Note: Despite the property name "authorId", this contains the author's GitHub handle
   * (username), not a numeric ID. This maintains backward compatibility with existing code.
   *
   * Example: { authorId: "urbanisierung", team: "Core" }
   *          where "urbanisierung" is the GitHub handle, not an ID
   */
  get allTeams(): Array<{ authorId: string; team: string }> {
    return Object.entries(this.teams).map(([authorHandle, team]) => ({
      authorId: authorHandle,
      team,
    }))
  }

  /**
   * Get unique team names
   */
  get uniqueTeams(): string[] {
    const teams = new Set(Object.values(this.teams))
    return Array.from(teams).sort()
  }

  /**
   * Get authors by team
   *
   * Returns a list of authors belonging to the specified team.
   * Note: The "id" field contains the GitHub handle, not a numeric ID.
   * The "handle" field may contain a different value if authors.json provides
   * a mapping, otherwise it falls back to the same value as "id".
   *
   * @param team - The team name to filter by
   * @returns Array of author objects with id (handle) and handle properties
   */
  getAuthorsByTeam(team: string): Array<{ id: string; handle: string }> {
    const authorHandles = Object.entries(this.teams)
      .filter(([, t]) => t === team)
      .map(([handle]) => handle)

    return authorHandles.map((handle) => ({
      id: handle,
      handle: this.getAuthor(handle) || handle,
    }))
  }

  /**
   * Get repositories by group/team name
   */
  getRepositoriesByGroup(groupName: string): string[] {
    return this.groups[groupName] || []
  }

  /**
   * Get all groups as an array
   */
  get allGroups(): Array<{ name: string; repositories: string[] }> {
    return Object.entries(this.groups).map(([name, repositories]) => ({
      name,
      repositories,
    }))
  }

  /**
   * Check if metadata is loaded
   */
  get isLoaded(): boolean {
    return (
      !this.isLoading &&
      this.loadError === null &&
      (Object.keys(this.authors).length > 0 || Object.keys(this.teams).length > 0)
    )
  }

  /**
   * Reset the store
   */
  reset(): void {
    this.isLoading = false
    this.loadError = null
    this.authors = {}
    this.teams = {}
    logger.info("MetadataStore reset", { component: "MetadataStore" })
  }
}
